// The coins of energy in the air. When the die of a roll for energy lands,
// one coin for each energy leaves the die, flies an arc over the page, and
// drops into its pip. Puffs of dither follow it. The layer covers the window
// and takes no click. `state/energyDelivery.ts` has the times, and
// `motion/energyFlight.ts` has the path. Garrett, 2026-10-12: "a little
// energy moudle that comes out of the die and goes bloop into the energy
// reserve hole through an animated arc".
// Energy a side pays goes the other way: a coin leaves each pip, from the
// last down, and flies to the slot of the skill it paid for. One ring opens
// around the slot when the last coin lands. `state/energySpend.ts` has the
// spend. "sound/effects when they are spent? same for enemy when enemy spends".
// Energy a capture gives leaves the square of the taken piece, on the beat of
// the capture, and drops into the pips the same way. `state/captureEnergy.ts`
// has the times.

import {
  ENERGY_COIN_FLIGHT_SCALE,
  ENERGY_COIN_SOURCE_LIFT,
  ENERGY_PUFF_LAG,
  ENERGY_PUFF_SIZES,
  ENERGY_SPEND_BEND,
  ENERGY_SPEND_COIN_SCALE,
  ENERGY_SPEND_FLIGHT_MS,
  ENERGY_SPEND_STEP_MS,
  REDUCED_MOTION_QUERY,
  SEAL_PING_LIFT,
  SEAL_PING_MS,
  SEAL_PING_REACH,
} from '../constants';
import type { Side } from '../engine/types';
import { energyOf, type RollRecord } from '../game/state';
import { el } from '../lib/dom';
import { flightPoint, flightScale, type Point } from '../motion/energyFlight';
import { playSound } from '../sound/player';
import { type Haul, haulStore } from '../state/captureEnergy';
import { type Delivery, deliveryStore } from '../state/energyDelivery';
import { type Spend, spendStore } from '../state/energySpend';
import { gameStore } from '../state/game';
import { isReviewing } from '../state/history';
import { shownEnergy } from '../state/rollShown';
import { squareTarget } from './Board';
import { skillTarget } from './CasterRow';
import { energyTarget } from './EnergyPips';
import { pingSeal, sealTarget } from './LastingSeals';

/** A coin that hits a seal ends at this part of the width of the seal. */
const SEAL_COIN_SHARE = 0.6;
/** A coin that pings off a seal ends at this part of the size it hit with. */
const SEAL_PING_END_SHARE = 0.5;

/** The dither of each puff, from the one nearest to the coin. */
const PUFF_TONES = ['solid', 'half', 'quarter'] as const;

const centerOf = (box: DOMRect): Point => ({
  x: box.left + box.width / 2,
  y: box.top + box.height / 2,
});

/** One coin in the air: how long it flies, how high its arc goes, its size at the end, and how much larger it is on the way. */
type Flight = { flightMs: number; bend: number; endSize: number; airScale: number };

/** The side whose energy sounds a fourth lower. */
const LOW_SIDE: Side = 'violet';

/** A part of one flight: the coin, or a puff behind it. */
type Part = { element: HTMLElement; lag: number; size: number };

/** Puts the middle of a part at `at`. The place is `left` and `top`, see `motes.css`. */
const place = ({ element }: Part, at: Point, size: number): void => {
  element.hidden = false;
  element.style.left = `${(at.x - size / 2).toFixed(1)}px`;
  element.style.top = `${(at.y - size / 2).toFixed(1)}px`;
  element.style.width = `${size.toFixed(1)}px`;
  element.style.height = `${size.toFixed(1)}px`;
};

/** `dieSlot` is the box on the stage where the die rests. */
export const createEnergyMotes = (dieSlot: HTMLElement): HTMLElement => {
  const root = el('div', { className: 'motes', ariaHidden: 'true' });
  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
  let roll: RollRecord | null = null;
  let launched = 0;

  /** The point the coins leave from: over the middle of the die, or the middle of the stage when the layout has no slot. */
  const source = (): Point | null => {
    const box = dieSlot.getBoundingClientRect();
    if (box.width > 0) {
      const center = centerOf(box);
      return { x: center.x, y: center.y - box.height * ENERGY_COIN_SOURCE_LIFT };
    }
    const stage = dieSlot.parentElement?.getBoundingClientRect();
    return stage && stage.width > 0 ? centerOf(stage) : null;
  };

  const fly = (
    side: Side,
    flight: Flight,
    from: Point,
    to: Point,
    onLand: (() => void) | null = null,
  ): void => {
    const parts: Part[] = [
      { element: el('i', { className: `motes__coin motes__coin--${side}` }), lag: 0, size: 1 },
      ...ENERGY_PUFF_SIZES.map((size, index) => ({
        element: el('i', {
          className: `motes__puff motes__puff--${PUFF_TONES[index] ?? 'quarter'} motes__puff--${side}`,
        }),
        lag: (index + 1) * ENERGY_PUFF_LAG,
        size,
      })),
    ];
    // The coin is the last child, so it lies over its puffs.
    for (const part of [...parts].reverse()) {
      part.element.hidden = true;
      root.append(part.element);
    }
    const startedAt = performance.now();
    const step = (): void => {
      const progress = (performance.now() - startedAt) / flight.flightMs;
      if (progress >= 1) {
        for (const part of parts) part.element.remove();
        onLand?.();
        return;
      }
      for (const part of parts) {
        const behind = progress - part.lag;
        if (behind < 0) continue;
        place(
          part,
          flightPoint(from, to, flight.bend, behind),
          flight.endSize * flightScale(flight.airScale, behind) * part.size,
        );
      }
      requestAnimationFrame(step);
    };
    step();
  };

  const launch = (delivery: Delivery, index: number): void => {
    const flight = delivery.flights[index];
    // The board shows an earlier step: the pips fill with no flight.
    if (!flight || !delivery.roll || isReviewing()) return;
    const side = delivery.roll.caster;
    // The state has the energy of the roll from the click. The first coin goes to the first pip that was empty.
    const firstEmpty = energyOf(gameStore.get(), side) - delivery.flights.length;
    const pip = energyTarget(side, firstEmpty + index);
    const from = source();
    playSound('energyOut', 0, index);
    if (!from) return;
    const flightMs = flight.landMs - flight.leaveMs;
    if (pip) {
      fly(
        side,
        { flightMs, bend: flight.bend, endSize: pip.width, airScale: ENERGY_COIN_FLIGHT_SCALE },
        from,
        centerOf(pip),
      );
      return;
    }
    // A seal covers the coins of the side. The coin hits the seal and pings off it.
    const seal = sealTarget(side);
    if (!seal) return;
    const hit = centerOf(seal);
    const size = seal.width * SEAL_COIN_SHARE;
    fly(
      side,
      { flightMs, bend: flight.bend, endSize: size, airScale: ENERGY_COIN_FLIGHT_SCALE },
      from,
      hit,
      () => pingOff(side, hit, seal.width, size),
    );
  };

  /** The coin jumps off the seal to one side, gets smaller, and goes. The seal jolts and the refusal sounds. */
  const pingOff = (side: Side, hit: Point, sealWidth: number, size: number): void => {
    pingSeal(side);
    playSound('refuse');
    if (reducedMotion.matches) return;
    const away = Math.random() < 1 / 2 ? -1 : 1;
    const to = {
      x: hit.x + away * sealWidth * SEAL_PING_REACH,
      y: hit.y - sealWidth * SEAL_PING_LIFT,
    };
    // In the air the coin has the size it hit with. It ends smaller, and goes.
    fly(
      side,
      {
        flightMs: SEAL_PING_MS,
        bend: 0,
        endSize: size * SEAL_PING_END_SHARE,
        airScale: 1 / SEAL_PING_END_SHARE,
      },
      hit,
      to,
    );
  };

  /** One ring that opens around a box, then leaves the page. */
  const ring = (side: Side, box: DOMRect): void => {
    const element = el('i', { className: `motes__ring motes__ring--${side}` });
    element.style.left = `${box.left}px`;
    element.style.top = `${box.top}px`;
    element.style.width = `${box.width}px`;
    element.style.height = `${box.height}px`;
    element.addEventListener('animationend', () => element.remove());
    root.append(element);
  };

  /** The energy a side paid for a skill flies from its pips to the slot of the skill. */
  const pay = (spend: Spend): void => {
    // The pips empty and sound in `EnergyPips.ts`. Energy that paid for no skill has no flight.
    if (spend.spellId === null || isReviewing()) return;
    const slot = skillTarget(spend.side, spend.spellId);
    if (!slot) return;
    const flies = !reducedMotion.matches;
    const to = centerOf(slot);
    for (let coin = 0; flies && coin < spend.count; coin += 1) {
      // The last pip pays first.
      const pip = energyTarget(spend.side, spend.before - 1 - coin);
      if (!pip) continue;
      const [low, high] = ENERGY_SPEND_BEND;
      const flight: Flight = {
        flightMs: ENERGY_SPEND_FLIGHT_MS,
        bend: low + (high - low) * Math.random(),
        endSize: pip.width,
        airScale: ENERGY_SPEND_COIN_SCALE,
      };
      setTimeout(() => fly(spend.side, flight, centerOf(pip), to), coin * ENERGY_SPEND_STEP_MS);
    }
    const lastLandsMs = flies
      ? (spend.count - 1) * ENERGY_SPEND_STEP_MS + ENERGY_SPEND_FLIGHT_MS
      : 0;
    playSound('energyPaid', lastLandsMs, spend.side === LOW_SIDE ? 1 : 0);
    if (flies) setTimeout(() => ring(spend.side, slot), lastLandsMs);
  };

  /** The coins of a capture that left, by the serial of its event. */
  const haulsLaunched = new Map<number, number>();

  /** One coin of a capture leaves the square of the taken piece for the next empty pip of the side. */
  const launchHaul = (haul: Haul, index: number): void => {
    const flight = haul.flights[index];
    if (!flight || isReviewing()) return;
    // The coins of the side in the air before this one each take a pip first.
    const inAir = haulStore
      .get()
      .filter((current) => current.side === haul.side)
      .reduce((sum, current) => sum + current.left - current.landed, 0);
    const pip = energyTarget(haul.side, shownEnergy(gameStore.get(), haul.side) + inAir - 1);
    const square = squareTarget(haul.square);
    playSound('energyOut', 0, index);
    if (!pip || !square) return;
    fly(
      haul.side,
      {
        flightMs: flight.landMs - flight.leaveMs,
        bend: flight.bend,
        endSize: pip.width,
        airScale: ENERGY_COIN_FLIGHT_SCALE,
      },
      centerOf(square),
      centerOf(pip),
    );
  };

  haulStore.subscribe((hauls) => {
    if (hauls.length === 0) haulsLaunched.clear();
    for (const haul of hauls) {
      let launched = haulsLaunched.get(haul.serial) ?? 0;
      while (launched < haul.left) {
        launchHaul(haul, launched);
        launched += 1;
      }
      haulsLaunched.set(haul.serial, launched);
    }
  });

  spendStore.subscribe((spend) => {
    if (spend) pay(spend);
  });

  deliveryStore.subscribe((delivery) => {
    if (delivery.roll !== roll) {
      roll = delivery.roll;
      launched = 0;
    }
    while (launched < delivery.left) {
      launch(delivery, launched);
      launched += 1;
    }
  });

  return root;
};
